import type { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { FileText, Handshake, MessagesSquare } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CaseSpotlight } from "@/components/verbandskommunikation/CaseSpotlight";
import { MonoLogoBar } from "@/components/logos/MonoLogoBar";
import { MegaphoneAnimation } from "@/components/verbandskommunikation/MegaphoneAnimation";
import { ConfettiOverlay } from "@/components/verbandskommunikation/ConfettiOverlay";
import { VimeoEmbed } from "@/components/media/VimeoEmbed";
import buerokratiePosterAsset from "@/assets/verbandskommunikation-buerokratie-poster.jpg.asset.json";
import { RotatingWord } from "@/components/ui/rotating-word";
import { lazySection } from "@/lib/lazy-section";
import {
  casePostQueryOptions,
  clientLogosQueryOptions,
  testimonialsQueryOptions,
} from "@/lib/public-content.queries";

// Kurznamen der Cases, deren Titelbilder aus den Case-Datensätzen bezogen werden –
// damit Seite und Case dauerhaft im Gleichlauf bleiben.
const PAPIER_CASE_SLUG = "saubere-haende-mit-papier";
const ALZHEIMER_CASE_SLUG = "deutsche-alzheimer-gesellschaft";
import analyseAsset from "@/assets/analyse-strategie.webp.asset.json";
import strategiePositionierungAsset from "@/assets/verbandskommunikation/strategie-positionierung.svg.asset.json";
import designAsset from "@/assets/design.webp.asset.json";
import socialMediaAsset from "@/assets/social-media-v2.webp.asset.json";
import sicherheitDurchErfahrungAsset from "@/assets/buergermeisterwahl/sicherheit-durch-erfahrung.png.asset.json";
import modularUndFairAsset from "@/assets/buergermeisterwahl/modular-und-fair.png.asset.json";
import direktmandatZuUnsAsset from "@/assets/buergermeisterwahl/direktmandat-zu-uns.png.asset.json";
import echterImpactAsset from "@/assets/echter-impact.webp.asset.json";
import erfahrungDynamikAsset from "@/assets/erfahrung-dynamik.webp.asset.json";
import tierschutzCoverAsset from "@/assets/cases/tierschutz-berlin/cover.webp.asset.json";
import tierschutzStrasseAsset from "@/assets/cases/tierschutz-berlin/plakat-strassenszene.webp.asset.json";
import tierschutzDoppelAsset from "@/assets/cases/tierschutz-berlin/plakat-doppel.webp.asset.json";
import kriseReputationAsset from "@/assets/krise-reputation.webp.asset.json";
import michaelBusserAsset from "@/assets/verbandskommunikation/krise-reputationsschutz-michael.png.asset.json";
import kampagneMobilisierungAsset from "@/assets/personalratswahl/creategy-mitglieder-3.svg.asset.json";
import executiveBrandingAsset from "@/assets/design-2.svg.asset.json";
import executiveInterviewAsset from "@/assets/executive-interview.webp.asset.json";
import presseMedienarbeitAsset from "@/assets/presse-public-affairs.webp.asset.json";
import mitgliederkommunikationAsset from "@/assets/creategy-newsletter-2.svg.asset.json";
import mitgliederwerbungAsset from "@/assets/gemeindekommunikation/standortmarketing-skyline-v2.svg.asset.json";
import { BookingCtaButton, INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";

const TestimonialsSlider = lazySection(async () => {
  const m = await import("@/components/home/TestimonialsSlider");
  return { default: m.TestimonialsSlider };
}, { minHeight: 520 });
const ContactCta = lazySection(async () => {
  const m = await import("@/components/verbandskommunikation/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 600 });
const FreebieTeaserSection = lazySection(async () => {
  const m = await import("@/components/freebie/FreebieTeaserSection");
  return { default: m.FreebieTeaserSection };
}, { minHeight: 320 });

type Baustein = {
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
  imageClassName?: string;
  coverImage?: string;
  coverImageAlt?: string;
  rightBottomImage?: string;
  rightBottomImageAlt?: string;
  leftBottomImage?: string;
  leftBottomImageAlt?: string;
  fullBleed?: boolean;
};

export const Route = createFileRoute("/politisch-kommunizieren/verbandskommunikation")({
  loader: async ({ context }) => {
    // Testimonials-Slider wird lazy nachgeladen → nur prefetchen, nicht awaiten.
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    // MonoLogoBar und die Case-Spotlights rendern serverseitig
    // mit useSuspenseQuery → müssen bereitstehen.
    await Promise.all([
      context.queryClient.ensureQueryData(clientLogosQueryOptions),
      context.queryClient.ensureQueryData(casePostQueryOptions(PAPIER_CASE_SLUG)),
      context.queryClient.ensureQueryData(casePostQueryOptions(ALZHEIMER_CASE_SLUG)),
    ]);
  },
  head: () => ({
    meta: [
      { title: "Verbandskommunikation | creategy" },
      {
        name: "description",
        content:
          "Wir stellen Verbände, Berufsorganisationen und NGOs ins Rampenlicht der Öffentlichkeit – mit politischem Verständnis und kommunikativer Schlagkraft.",
      },
      { property: "og:title", content: "Verbandskommunikation | creategy" },
      {
        property: "og:description",
        content:
          "Wir stellen Verbände, Berufsorganisationen und NGOs ins Rampenlicht der Öffentlichkeit – mit politischem Verständnis und kommunikativer Schlagkraft.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/politisch-kommunizieren/verbandskommunikation" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Verbandskommunikation | creategy" },
      {
        name: "twitter:description",
        content:
          "Wir stellen Verbände, Berufsorganisationen und NGOs ins Rampenlicht der Öffentlichkeit – mit politischem Verständnis und kommunikativer Schlagkraft.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://creategy.de/politisch-kommunizieren/verbandskommunikation" },
    ],
  }),
  component: VerbandskommunikationPage,
});

function BausteinVisual({ baustein }: { baustein: Baustein }) {
  const imageSrc = baustein.image?.trim() || baustein.coverImage?.trim();
  const imageAlt = baustein.image ? baustein.imageAlt : baustein.coverImageAlt;

  if (imageSrc) {
    if (baustein.fullBleed) {
      return (
        <div className="-mx-6 -mb-6 mt-auto flex items-end justify-center md:-mx-8 md:-mb-8">
          <img
            src={imageSrc}
            alt={imageAlt ?? ""}
            className={`block h-[9.5rem] w-auto max-w-full object-contain object-bottom md:h-[12rem]${baustein.imageClassName ? ` ${baustein.imageClassName}` : ""}`}
            loading="lazy"
            decoding="async"
          />
        </div>
      );
    }
    return (
      <div className={`-mx-6 -mb-8 mt-auto flex h-[9.5rem] shrink-0 items-end justify-center px-4 md:-mx-8 md:-mb-10 md:h-[12rem] md:px-6${baustein.imageClassName ? ` ${baustein.imageClassName}` : ""}`}>
        <img
          src={imageSrc}
          alt={imageAlt ?? ""}
          className="block h-full w-full object-contain object-bottom"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      className="-mx-6 -mb-10 mt-auto grid min-h-0 flex-1 grid-cols-4 items-end gap-2 border-t border-brand-kommunikation/10 bg-brand-kommunikation/5 px-6 pb-8 md:-mx-8 md:-mb-12 md:px-8"
    >
      {[45, 78, 58, 92].map((height) => (
        <span
          key={height}
          className="rounded-t bg-brand-kommunikation/45"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}


type Spotlight = {
  key: string;
  subline: string;
  title: string;
  text: string;
  ctaTo: string;
  quote?: { text: string; author: string };
  media: ReactNode;
};

function SpotlightsSection() {
  // Papierindustrie- und Alzheimer-Case ziehen ihre Titelbilder aus den Case-Datensätzen
  // (Kurzname), damit Seite und Case dauerhaft im Gleichlauf bleiben. Die übrigen Einträge
  // behalten ihre individuellen Medien (Bild-Collage bzw. Video) fest auf der Seite.
  const { data: papierCase } = useSuspenseQuery(casePostQueryOptions(PAPIER_CASE_SLUG));
  const { data: alzheimerCase } = useSuspenseQuery(casePostQueryOptions(ALZHEIMER_CASE_SLUG));

  const spotlights: Spotlight[] = [
    {
      key: "tierschutz",
      subline: "Tierschutzverein für Berlin",
      title: "185 Jahre Tierschutz für Berlin.",
      text: "Zum Jubiläum des ältesten Tierschutzvereins Deutschlands haben wir eine berlinweite Kampagne entwickelt: emotionale Motive, eine unverwechselbare Bildsprache und Sichtbarkeit im gesamten Stadtbild. Eine Kampagne, die man auf der Straße sieht und im Herzen behält.",
      ctaTo: "/cases/185-jahre-tierschutz-fuer-berlin",
      media: (
        <div className="grid grid-cols-3 gap-4">
          {[
            { src: tierschutzCoverAsset.url, alt: "Tierschutzverein Berlin – Kampagnenmotiv 'Potz-Blitz' zum Tierschutz-Abi" },
            { src: tierschutzStrasseAsset.url, alt: "Großflächenplakat '10.000 Miezen auf Achse' im Berliner Stadtraum" },
            { src: tierschutzDoppelAsset.url, alt: "Doppelplakat der Jubiläumskampagne '185 Jahre Tierschutz für Berlin'" },
          ].map((item, index) => (
            <div
              key={item.src}
              className={`overflow-hidden rounded-md bg-secondary/40 ${index === 0 ? "col-span-2 row-span-2" : ""}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="h-full min-h-[11rem] w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      key: "insm",
      subline: "INSM",
      title: "Ein digitales Werkzeug gegen sinnlose Bürokratie.",
      text: "Für die Initiative Neue Soziale Marktwirtschaft haben wir ein Tool konzipiert und umgesetzt, mit dem Bürgerinnen und Bürger überflüssige Bürokratie melden können. Die Meldungen gehen direkt an Bundestagsabgeordnete, die konkret handeln können. Kampagne, Produkt und politischer Hebel in einem.",
      quote: {
        text: "Während andere über Bürokratieabbau nur reden, handeln wir, gemeinsam mit Politikern. Eine Art Mischung aus ‚Tinder' und ‚Abgeordnetenwatch', entwickelt von creategy. Wir erleichtern Politikern parteiübergreifend und konkret, beim Bürokratieabbau mitzumachen.",
        author: "Thorsten Alsleben, Geschäftsführer INSM",
      },
      ctaTo: "/cases/ein-digitales-werkzeug-gegen-sinnlose-buerokratie",
      media: (
        <VimeoEmbed
          videoId="1211996656"
          title="Ein digitales Werkzeug gegen sinnlose Bürokratie"
          posterUrl={buerokratiePosterAsset.url}
        />
      ),
    },
  ];

  // Fehlt der Case oder sein Titelbild, entfällt der Eintrag – kein leerer Rahmen.
  if (papierCase?.cover_url) {
    spotlights.push({
      key: "papierindustrie",
      subline: "DIE PAPIERINDUSTRIE e. V.",
      title: "Saubere Hände mit Papier!",
      text: "Das Handzeichen der Branche gibt es seit Jahrzehnten, seine Formensprache wirkte neben modernen Marken jedoch datiert. Für die Vereinigung Hygiene-Papiere haben wir es in ein zeitgemäßes Key Visual übersetzt, dazu eine frische Farbwelt, eine eigene Schrift, eine überarbeitete Website und den vollständigen Auftritt auf der Fachmesse CMS Berlin.",
      ctaTo: `/cases/${PAPIER_CASE_SLUG}`,
      media: (
        <div className="overflow-hidden rounded-md bg-secondary/40">
          <img
            src={papierCase.cover_url}
            alt={papierCase.title}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      ),
    });
  }

  // Fehlt der Case oder sein Titelbild, entfällt der Eintrag – kein leerer Rahmen.
  if (alzheimerCase?.cover_url) {
    spotlights.push({
      key: "alzheimer",
      subline: "Deutsche Alzheimer Gesellschaft",
      title: "Mit einer Stimme sprechen.",
      text: "Für Vorstand und Geschäftsführung des Dachverbands haben wir ein Kommunikations- und Rhetoriktraining entwickelt: eine gemeinsame Kernbotschaft für den ganzen Verband und konkretes Handwerkszeug für Kamera, Radio, Podium und das persönliche Gespräch. Ein Tag, der aus vielen einzelnen Stimmen eine verbandliche Stimme macht.",
      ctaTo: `/cases/${ALZHEIMER_CASE_SLUG}`,
      media: (
        <div className="overflow-hidden rounded-md bg-secondary/40">
          <img
            src={alzheimerCase.cover_url}
            alt={alzheimerCase.title}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      ),
    });
  }

  return (
    <section aria-labelledby="spotlights">
      <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <h2 id="spotlights" className="heading-h2 text-brand-kommunikation">
            Projekte, die zeigen, wie wir arbeiten.
          </h2>
        </Reveal>
        <div className="mt-16 space-y-24 md:space-y-32">
          {spotlights.map((spotlight, index) => (
            <CaseSpotlight
              key={spotlight.key}
              subline={spotlight.subline}
              title={spotlight.title}
              text={spotlight.text}
              quote={spotlight.quote}
              ctaTo={spotlight.ctaTo}
              ctaLabel="Projekt entdecken"
              mediaLeft={index % 2 === 0}
            >
              {spotlight.media}
            </CaseSpotlight>
          ))}
        </div>
      </div>
    </section>
  );
}


function VerbandskommunikationPage() {
  const gruende = [
    {
      title: "Sicherheit durch Erfahrung.",
      text: "Über zweihundert Wahlkämpfe und politische Kampagnen, kein Auftrag, der nicht politisch ist. Wer zu uns kommt, bekommt ein Team, das die Mechanismen des politischen Raums aus eigener Erfahrung kennt.",
      image: sicherheitDurchErfahrungAsset.url,
      imageAlt: "Strategieentwicklung auf Whiteboard als Symbol für politische Erfahrung",
    },
    {
      title: "Echter Impact.",
      text: "Gute Kommunikation platziert Themen und erzielt Resultate. Wir wissen, wie Botschaften im politischen Raum präzise gesteuert werden müssen, um Gehör zu finden: bei Mitgliedern, Medien und Mandatsträgern.",
      image: echterImpactAsset.url,
      imageAlt: "Zwei creategy-Berater im konzentrierten Gespräch",
    },
    {
      title: "Erfahrung trifft Dynamik.",
      text: "Wir kombinieren strategische Erfahrung mit der Agilität moderner Kampagnenführung. Krisenfestes Fundament, reichweitenstarke Formate und Botschaften, die Ihre Zielgruppe dort erreichen, wo Meinung entsteht.",
      image: erfahrungDynamikAsset.url,
      imageAlt: "Erfahrener Berater und junger Stratege im Austausch",
    },
    {
      title: "Modular und fair.",
      text: "Sie buchen nur, was Sie brauchen. Kein aufgeblähtes Paket, keine versteckten Kosten, kein Upselling im falschen Moment. Transparentes Pricing, das zu Ihrer Organisation und Ihrem Budget passt.",
      image: modularUndFairAsset.url,
      imageAlt: "Bunte Holzbausteine als Symbol für modulare und faire Beratung",
    },
    {
      title: "Ihr Direktmandat zu uns.",
      text: 'Kein Ticketsystem, keine Warteschleife, kein „Ich leite das weiter." Bei creategy hat Ihr Verband einen festen Ansprechpartner: erreichbar per Telefon, Mail und WhatsApp.',
      image: direktmandatZuUnsAsset.url,
      imageAlt: "Persönlicher Ansprechpartner am Telefon",
    },
  ];

  const gruendeImages = gruende.map((card) => ({ src: card.image, alt: card.imageAlt }));

  return (
    <Layout>

      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div className="relative">
              <h1
                id="hero"
                className="heading-h1 relative z-10 text-brand-kommunikation md:w-[145%]"
              >
                Starke Kommunikation<br />
                für{" "}
                <RotatingWord
                  words={["Verbände.", "NGOs.", "Stiftungen.", "Vereine."]}
                />
              </h1>
              <p
                className="fade-in-up mt-10 font-body text-base leading-relaxed text-foreground md:text-lg"
                style={{ animationDelay: "160ms" }}
              >
                Strategische Positionierung, starkes Branding und schlagkräftige Kampagnen für Organisationen, die den Diskurs bestimmen wollen.
              </p>
              <div
                className="fade-in-up mt-10 flex flex-col items-stretch gap-3 lg:flex-row lg:items-center"
                style={{ animationDelay: "240ms" }}
              >
                <Link
                  to="/kontakt"
                  search={{ anliegen: "verbandskommunikation" }}
                  className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 lg:w-auto"
                >
                  {INQUIRY_CTA_LABEL}
                </Link>
                <BookingCtaButton variant="secondary" eventPath="creategy/erstgespraech" />
              </div>
            </div>

            <div className="order-first md:order-none">
              <MegaphoneAnimation />
            </div>
          </div>
        </div>
      </section>

      <MonoLogoBar fixedCategory="verbandskommunikation" bordered={false} />

      <section
        aria-labelledby="warum-verbandskommunikation"
        className="relative overflow-hidden"
      >
        <ConfettiOverlay />
        <div className="section-y relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div>
            <Reveal>
              <h2
                id="warum-verbandskommunikation"
                lang="de"
                className="heading-h2 text-brand-kommunikation hyphens-auto-de md:[hyphens:none]"
              >
                Warum Verbandskommunikation eine politische Agentur braucht.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="heading-lead">
                <h3 className="heading-h3 text-brand-kommunikation">
                  Ihre Themen entscheiden sich im politischen Raum.
                </h3>
                <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                  Verbände kommunizieren selten zum Selbstzweck. Es geht um Gesetzgebung, um öffentliche Meinung, um die Mobilisierung der eigenen Mitglieder. Klassische PR-Agenturen kennen die Medienlandschaft. Wer aber Positionen politisch durchsetzen will, braucht darüber hinaus ein Gespür dafür, wie Entscheidungen in Parlamenten, Ministerien und Fraktionen tatsächlich entstehen.
                </p>
                <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                  Genau dieses Gespür bringen wir mit. creategy arbeitet ausschließlich politisch: in über zweihundert Wahlkämpfen, in Kampagnen für Verbände und in der laufenden Kommunikation politischer Organisationen. Diese Erfahrung fließt in jede Positionierung, jede Kampagne und jeden Post, den wir für Ihren Verband entwickeln.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>


      <section aria-labelledby="verbandskommunikation-video">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="mx-auto w-full max-w-5xl">
              <VimeoEmbed
                videoId="1243291364"
                title="Was Verbände & Institutionen beachten sollten"
                posterUrl="https://i.vimeocdn.com/video/2221771566-89facc520de226140dd7fc00a6b2560164f6ea32b36d23f34c7473f8ef15bf81-d_1280?region=us"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <SpotlightsSection />


      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-kommunikation"
            >
              Unsere Leistungen für Ihren Verband.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 w-full font-body text-base leading-relaxed text-foreground md:text-lg">
              Verbände brauchen Kommunikation, die Ziele erreicht: in der Gesetzgebung, in der Mitgliedermobilisierung und bei der Schärfung des eigenen Profils. Sie buchen bei uns nur, was Sie wirklich brauchen. Alle Bausteine kommen aus einer Hand und lassen sich flexibel kombinieren.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {([
              { title: "Strategie & Positionierung", text: "Wofür steht Ihr Verband, wen wollen Sie erreichen und womit? Wir analysieren Ihre Ausgangslage und entwickeln eine Positionierung, die Ihre Kommunikation über alle Kanäle hinweg trägt.", rightBottomImage: strategiePositionierungAsset.url, rightBottomImageAlt: "Illustration: Strategie & Positionierung" },
              { title: "Kampagnen & Mobilisierung", text: "Ob Unterstützung von Gesetzesvorhaben oder Abwehr von Überregulierung: Wir konzipieren Kampagnen, die Resonanz erzeugen und Druck aufbauen. Wir aktivieren Mitglieder und Öffentlichkeit punktgenau für Ihre Anliegen.", image: kampagneMobilisierungAsset.url, imageAlt: "Illustration eines Netzwerks aus verbundenen Personen als Symbol für Mobilisierung", imageClassName: "max-h-[12rem] md:max-h-[13rem] lg:max-h-[12rem]" },
              { title: "Branding & Design", text: "Wir entwickeln Markenidentitäten, die Ästhetik mit System verbinden. Ein Corporate Design für Verbände muss auf allen Ebenen funktionieren.", image: executiveBrandingAsset.url, imageAlt: "Illustration als Symbol für Branding und Design", fullBleed: true },
              { title: "Social Media & Content", text: "Wir positionieren Ihre Themen digital und erschließen neue Zielgruppen. Von der Kanalstrategie über Templates bis zur laufenden Betreuung.", image: socialMediaAsset.url, imageAlt: "Like- und Reaktions-Icons als Symbol für Social-Media-Content", fullBleed: true, imageClassName: "!w-full !h-auto !object-cover md:!w-auto md:!h-[12rem] md:!object-contain md:-mb-6 lg:mb-0" },
              { title: "Executive Branding", text: "Präsidenten und Hauptgeschäftsführer (m/w/d) sind die Gesichter ihres Verbandes. Wir positionieren Ihre Führungsspitze als einflussreiche Stimme im digitalen und politischen Diskurs.", image: executiveInterviewAsset.url, imageAlt: "Zwei Interviewsessel mit Studio-Mikrofonen als Symbol für Executive Branding", fullBleed: true },
              { title: "Mitgliederkommunikation", text: "Ein Verband ist so stark wie die Bindung zu seinen Mitgliedern. Wir entwickeln Newsletter, Magazine und interne Formate, die aktivieren und einen Mehrwert bieten.\u00a0", image: mitgliederkommunikationAsset.url, imageAlt: "Bunte Sprechblasen als Symbol für Mitgliederkommunikation" },
              { title: "Presse & Public Affairs", text: "Wir übersetzen Ihre Verbandspositionen in den politischen und medialen Raum: mit Pressearbeit, Positionspapieren und modernen Formaten, die Ihre Themen zur Zielgruppe transportieren.", image: presseMedienarbeitAsset.url, imageAlt: "Hände halten Süddeutsche Zeitung, Frankfurter Allgemeine, Die Welt und ein Mikrofon als Symbol für Presse- und Public-Affairs-Arbeit", imageClassName: "md:h-[12rem] lg:h-[12rem] lg:-mb-10" },
              { title: "Krise & Reputationsschutz", text: "Schnelles Issue Management und proaktive Kommunikation halten Ihren Verband auch in kritischen Schlagzeilen handlungsfähig.", leftBottomImage: michaelBusserAsset.url, leftBottomImageAlt: "Illustration: Krise & Reputationsschutz" },
              { title: "Mitgliederwerbung", text: "Ihr Verband ist so stark wie seine Basis. Wir entwickeln Kampagnen zur Mitgliedergewinnung, die Ihre Zielgruppen dort erreichen, wo sie sind.", image: mitgliederwerbungAsset.url, imageAlt: "Illustration einer Stadtsilhouette als Symbol für Mitgliederwerbung", fullBleed: true, imageClassName: "pb-6 md:pb-8" },
            ] as Baustein[]).map((b, i) => {
              return (
                <Reveal key={b.title} delay={i * 60} className="h-full">

                  {b.rightBottomImage || b.leftBottomImage ? (
                    <article
                      className={[
                        "grid min-h-[20rem] overflow-hidden rounded-md bg-brand-kommunikation text-background md:h-[24rem]",
                        b.leftBottomImage
                          ? "grid-cols-[minmax(0,2fr)_minmax(0,3fr)] [grid-template-areas:'title_title''image_text'] [grid-template-rows:auto_1fr]"
                          : "grid-cols-[minmax(0,3fr)_minmax(0,2fr)]",
                      ].join(" ")}
                    >
                      {b.leftBottomImage ? (
                        <>
                          <div className="[grid-area:title] p-6 pb-0 md:p-8 md:pb-0">
                            <h3 className="heading-h3 text-background">
                              {b.title}
                            </h3>
                          </div>
                          <div className="relative flex h-full items-end justify-start overflow-hidden pt-6 [grid-area:image] md:pt-8">
                            <img
                              src={b.leftBottomImage}
                              alt={b.leftBottomImageAlt ?? ""}
                              className="block h-full w-full object-contain object-left-bottom"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div className="flex min-w-0 flex-col p-6 pt-4 [grid-area:text] md:p-8 md:pt-4">
                            <p className="font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                              {b.text}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex min-w-0 flex-col p-6 md:p-8">
                            <h3 className="heading-h3 text-background">
                              {b.title}
                            </h3>
                            <p className="mt-4 min-w-0 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                              {b.text}
                            </p>
                          </div>
                          {b.rightBottomImage ? (
                            <div className="relative flex h-full items-end justify-end overflow-hidden pt-6 md:pt-8">
                              <img
                                src={b.rightBottomImage}
                                alt={b.rightBottomImageAlt ?? ""}
                                className="block h-full w-full object-contain object-right-bottom"
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                          ) : null}
                        </>
                      )}
                    </article>

                  ) : (
                    <article className="flex h-[20rem] flex-col overflow-hidden rounded-md bg-brand-kommunikation p-6 text-background md:h-[24rem] md:p-8">
                      <h3 className="heading-h3 text-background">
                        {b.title}
                      </h3>
                      <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                        {b.text}
                      </p>
                      <BausteinVisual baustein={b} />
                    </article>
                  )}
                </Reveal>
              );
          })}
          </div>
        </div>
      </section>

      

      <TestimonialsSlider
        showFilters={false}
        fixedCategory="verbandskommunikation"
        headingColor="text-brand-kommunikation"
        title={
          <>
            Das sagen unsere{" "}
            Kundinnen und Kunden.
          </>
        }
      />

      <section
        aria-labelledby="gruende"
        className="relative isolate z-0 bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2 id="gruende" className="heading-h2 text-brand-kommunikation">
                5 Gründe für creategy
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="mt-4 font-body text-sm leading-relaxed text-foreground/85 md:text-base">
                Warum Verbände, NGOs, Stiftungen und Vereine sich für creategy entscheiden: fünf Gründe, die in der Kommunikation den Unterschied machen.
              </p>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => {
              const img = gruendeImages[i];
              return (
                <li
                  key={card.title}
                  className="md:sticky md:top-32 md:pb-[6.66vh]"
                  style={{ zIndex: 10 + i }}
                >
                  <article className="grid gap-8 rounded border border-brand-kommunikation/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,131,153,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                    <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-kommunikation/20 bg-brand-kommunikation/5">
                      {img.src ? (
                        <img
                          src={img.src}
                          alt={img.alt ?? ""}
                          className="h-full w-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center">
                          <span className="font-display text-7xl font-extrabold text-brand-kommunikation/30 md:text-8xl">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-display text-lg font-extrabold uppercase text-brand-kommunikation/60">
                            creategy
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex h-full flex-col justify-center">
                      <div>
                        <h3 className="heading-h3 text-brand-kommunikation">
                          {card.title}
                        </h3>
                        <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                          {card.text}
                        </p>
                      </div>
                      <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-kommunikation/60">
                        Grund {i + 1} von {gruende.length}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <FreebieTeaserSection
        slug="positionspapier-wirkung"
        kennung="verbandskommunikation-positionspapier"
        accentClass="text-brand-kommunikation"
        heading="Kostenlos: Positionspapier mit Wirkung."
        text="Eine gute Fachposition setzt sich nicht von allein durch. Der Leitfaden zeigt Schritt für Schritt, wie Verbände aus einer Fachposition ein Papier machen, das in Politik und Redaktionen tatsächlich gelesen wird: Aufbau, Sprache, Zeitpunkt und Ansprache der richtigen Adressaten. Als PDF zum Herunterladen, ohne Kosten, ohne Verpflichtung."
      />

      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess" className="heading-h2 text-brand-kommunikation">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline
              headingColor="text-brand-kommunikation"
              steps={[
                {
                  title: "Kostenloses Erstgespräch",
                  text: "Wir analysieren Ihre aktuelle Situation, identifizieren Ihre kommunikativen Hebel und definieren klare Ziele für Ihren Verband. Digital oder bei Ihnen in der Geschäftsstelle.",
                  Icon: MessagesSquare,
                },
                {
                  title: "Ihr individuelles Konzept",
                  text: "Auf Basis des Gesprächs entwickeln wir ein maßgeschneidertes Konzept: präzise auf Ihre Strukturen zugeschnitten und transparent kalkuliert. Ob laufende Betreuung, einzelnes Projekt oder strategisches Sparring entscheiden Sie.",
                  Icon: FileText,
                },
                {
                  title: "Ihre Entscheidung",
                  text: "Sie erhalten einen klaren Fahrplan für Ihre Kommunikation, Ihre Kampagne oder Ihr Branding. Sie entscheiden in Ruhe und mit allen Gremien, die dazugehören. Wenn Sie Ja sagen, legen wir los.",
                  Icon: Handshake,
                },
              ]}
            />
          </div>
        </div>
      </section>
      <ContactCta />
    </Layout>
  );
}
